import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  linkWithCredential,
  unlink,
  updatePhoneNumber,
} from "firebase/auth";
import { doc, getDocFromServer } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase/firebase";

const INSTALLATION_ID_KEY = "stichb_quote_phone_installation_id";

export function getQuoteInstallationId() {
  const existing = window.localStorage.getItem(INSTALLATION_ID_KEY) || "";
  if (/^[A-Za-z0-9-]{20,80}$/.test(existing)) return existing;

  const created = window.crypto.randomUUID();
  window.localStorage.setItem(INSTALLATION_ID_KEY, created);
  return created;
}

const normalizePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  const phone = digits.length > 10 ? digits.slice(-10) : digits;
  return /^[6-9]\d{9}$/.test(phone) ? phone : "";
};

export async function hasValidQuotePhoneVerification(phoneNumber) {
  const user = auth.currentUser;
  const phone = normalizePhone(phoneNumber);
  if (!user || !phone) return false;

  const installationId = getQuoteInstallationId();
  const snapshot = await getDocFromServer(
    doc(db, "quote_phone_verifications", `${user.uid}_${installationId}`),
  );
  if (!snapshot.exists()) return false;

  const data = snapshot.data();
  return data.customerId === user.uid &&
    data.phoneNumber === phone &&
    data.expiresAt?.toDate?.().getTime() > Date.now();
}

export function createQuotePhoneRecaptcha(containerId) {
  return new RecaptchaVerifier(auth, containerId, { size: "invisible" });
}

export async function sendQuotePhoneOtp(phoneNumber, verifier) {
  const phone = normalizePhone(phoneNumber);
  if (!phone) throw new Error("Enter a valid 10-digit Indian mobile number.");
  const provider = new PhoneAuthProvider(auth);
  return provider.verifyPhoneNumber(`+91${phone}`, verifier);
}

export async function verifyQuotePhoneOtp({ phoneNumber, verificationId, smsCode }) {
  const user = auth.currentUser;
  const phone = normalizePhone(phoneNumber);
  if (!user) throw new Error("Please sign in before verifying your phone number.");
  if (!phone || !/^\d{6}$/.test(String(smsCode || "").trim())) {
    throw new Error("Enter the valid 6-digit OTP.");
  }

  const credential = PhoneAuthProvider.credential(verificationId, String(smsCode).trim());
  const existingPhone = normalizePhone(user.phoneNumber);
  if (existingPhone !== phone) {
    const hasPhoneProvider = user.providerData.some((provider) => provider.providerId === "phone");
    if (hasPhoneProvider) {
      await updatePhoneNumber(user, credential);
    } else {
      await linkWithCredential(user, credential);
    }
  }
  await user.getIdToken(true);

  const deviceInstallationId = getQuoteInstallationId();
  await httpsCallable(functions, "certifyQuotePhoneVerification")({
    phoneNumber: phone,
    deviceInstallationId,
  });
  if (user.providerData.some((provider) => provider.providerId === "phone") && user.providerData.length > 1) {
    await unlink(user, "phone");
    await user.getIdToken(true);
  }
  return deviceInstallationId;
}

export async function requireValidQuotePhoneVerification(phoneNumber) {
  if (!await hasValidQuotePhoneVerification(phoneNumber)) {
    throw new Error("Verify this phone number by OTP before requesting a quotation.");
  }
  return getQuoteInstallationId();
}
